/**
 * 订单状态 —— 单一状态机(M-03)。**单点定义在这里,三端引用。**
 *
 * 与契约 components.schemas.OrderStatus 的 enum 逐字一致,顺序也一致 ——
 * 顺序不是装饰:主链上的状态按流转次序排,读的人能一眼看出「现在到哪了」。
 * 取消与退款是旁路态,排在最后,不占主链。
 *
 * ═══ 为什么两条业务线合用一个状态机 ═══
 *
 * M-01:一张 order 表用 serviceType 区分纯运输与全流程代办。
 * 代办线在 paid 之后插入 agency_processing → agency_done 两态,
 * 之后与纯运输线完全相同(运输段一模一样)。
 * 两套独立状态机的代价是每个端都要认两套,而它们有 11 个态是重复的。
 *
 * ═══ S-04 只用到前两个态 ═══
 *
 * 下单落 pending_payment(支付是 S-05)。其余状态的**流转规则**在
 * apps/api/src/modules/order/order.state.ts,那里的边表是 M-03 的落点。
 * 这里只定义「有哪些状态」,不定义「谁能到谁」—— 后者要在服务端才有意义。
 */
export const ORDER_STATUS = [
  'created',
  'pending_payment',
  'paid',
  'agency_processing',
  'agency_done',
  'pending_accept',
  'accepted',
  'picked_up',
  'in_transit',
  'delivered',
  'drone_returned',
  'completed',
  'cancelled',
  'refunding',
  'refunded',
] as const

export type OrderStatus = (typeof ORDER_STATUS)[number]

export function isOrderStatus(x: unknown): x is OrderStatus {
  return typeof x === 'string' && (ORDER_STATUS as readonly string[]).includes(x)
}

// ───────────────────────────────────────────────────────
// 「我的订单」的分段(2026-10-11 人裁决,AS-053 / 决策 Y-26;
// 2026-10-18 加角色档 received,决策 Y-49)
//
// 契约 GET /api/orders 的 group 查询参数取下面这几个值,**分段在服务端过滤**:
// 客户端拉一页回来再本地分段时,列表是分页的 —— 客户有 50 单时「已完成」
// 只统计已加载的那一页,数字不对而页面没有任何提示。归属从 ORDER_STATUS
// 推导一次、只在这里推导,三端(契约 enum / 服务端 where / 小程序分段控件)引用。
//
// ═══ 两组不同性质的分组共用一个参数 ═══
//
//   · 状态档(all / in_progress / completed):按**状态**划分。归属规则见下。
//   · 角色档(received):按**我是谁**划分 —— 我是收件人的单(Y-49)。
//
// received 是唯一一个按角色而不是按状态划分的取值:它问的是
// 「这一单的 recipientPhone 是不是我」,与状态无关,所以它**不经过
// orderStatusesInGroup**(那个函数只认状态档)。把它塞进状态档那一支
// 会静默返回一份「进行中」的清单 —— 那是错的,而且不报错。
//
// 归属规则:
//   · 已完成 = 终态(ORDER_TERMINAL_STATUS 那五个)。**已取消 / 已退款也在这一档** ——
//     它们不是「顺利完成」,但更不是进行中;放进进行中会让客户以为这一单还在跑
//     (那一种错更贵)。
//   · 进行中 = ORDER_STATUS 去掉终态的**差集** —— 新加一个状态时它自动落进行中,
//     而不是两个分段都进不去(那等于把这一单对客户静默隐藏)。
// ───────────────────────────────────────────────────────

/**
 * 客户订单列表的全部 group 取值 —— 状态档三个 + 角色档一个(Y-49)。
 *
 * 契约 GET /api/orders 的 group enum 与它**逐字一致**(guards/check-customer-order-chain.mjs
 * 双向核对)。往里加一个取值之前先想清楚它是状态档还是角色档。
 */
export const ORDER_GROUP = ['all', 'in_progress', 'completed', 'received'] as const

export type OrderGroup = (typeof ORDER_GROUP)[number]

/**
 * 角色档「我收的」(Y-49)—— 与「我寄的」(下单单人自己)相对。
 * 单点定义:服务端 where、DTO 校验、小程序分段控件都从这里取,不各写一份字符串。
 */
export const ORDER_GROUP_RECEIVED = 'received'

/**
 * 飞手「我抢到的订单」的分段取值(AS-058)。
 *
 * 与客户侧的状态档同名同义,但**没有角色档 received** —— 飞手列表按
 * pilotUserId 过滤,「我收的」在那一侧没有意义。飞手契约的 group enum
 * 与它逐字一致(guards/check-pilot-work-chain.mjs)。
 *
 * 为什么不直接复用 ORDER_GROUP:客户侧加 received 时,复用会让飞手契约
 * 也凭空多出一个它并不支持的取值 —— 客户端发得出、服务端不认,
 * 或者更糟:服务端认了却按状态档处理(静默返回错的清单)。
 */
export const PILOT_ORDER_GROUP = ['all', 'in_progress', 'completed'] as const

export type PilotOrderGroup = (typeof PILOT_ORDER_GROUP)[number]

/**
 * 终态集合 —— 进了这些状态这一单就结束了(不再有出边,M-03 的边表)。
 * `satisfies` 保证每一个取值都是真实的状态:少一个会让那批单落进「进行中」
 * (客户以为还在跑),多一个写错名字时编译期当场红。
 */
export const ORDER_TERMINAL_STATUS = [
  'delivered',
  'drone_returned',
  'completed',
  'cancelled',
  'refunded',
] as const satisfies readonly OrderStatus[]

export function isTerminalOrderStatus(status: OrderStatus): boolean {
  return (ORDER_TERMINAL_STATUS as readonly string[]).includes(status)
}

/**
 * **状态档**(进行中 / 已完成)—— 排除 all(不过滤)与 received(角色档)。
 * orderStatusesInGroup 只接受它,所以「把 received 当状态档用」在编译期就被挡住。
 */
export type OrderStatusGroup = Exclude<OrderGroup, 'all' | typeof ORDER_GROUP_RECEIVED>

export const ORDER_STATUS_GROUP = ['in_progress', 'completed'] as const satisfies readonly OrderStatusGroup[]

/**
 * 一个状态分组里的全部状态(服务端 where 的输入)。
 * 「all」不是一个分组(它就是不过滤)、「received」是角色档(不经状态),
 * 两者都传不进这个函数 —— 类型挡住。
 */
export function orderStatusesInGroup(group: OrderStatusGroup): OrderStatus[] {
  return group === 'completed'
    ? [...ORDER_TERMINAL_STATUS]
    : ORDER_STATUS.filter((s) => !isTerminalOrderStatus(s))
}
